import React, { useRef, useLayoutEffect, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Section } from '../styles/CommonElements';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const PROCESS_PHASES = [
  {
    id: "process-1",
    title: "Discovery & Requirements",
    description:
      "We begin by deeply understanding your business challenges, workflows, and goals. Through collaborative sessions, we document your exact requirements and establish a clear vision for your custom solution.",
  },
  {
    id: "process-2",
    title: "Architecture & Planning",
    description:
      "Our team designs a scalable system architecture tailored to your needs. We create detailed technical blueprints and project timelines, ensuring every decision supports long-term growth and maintainability.",
  },
  {
    id: "process-3",
    title: "Design & Prototyping",
    description:
      "We craft intuitive user interfaces that prioritize functionality and user experience. Interactive prototypes let you visualize and refine the solution before development begins.",
  },
  {
    id: "process-4",
    title: "Development & Testing",
    description:
      "Using modern technologies and best practices, our developers build your solution with clean, maintainable code. Rigorous testing ensures reliability, security, and exceptional performance.",
  },
  {
    id: "process-5",
    title: "Deployment & Support",
    description:
      "We handle seamless deployment with minimal disruption to your operations. Comprehensive training empowers your team, and ongoing support ensures your software continues to evolve with your business.",
  },
];

const ProcessSection = styled(Section)`
  background-color: transparent;
  padding: 6rem 0 2rem 0;
  overflow: visible;
  
  @media (max-width: 768px) {
    padding: 4rem 0 1rem 0;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const LeftColumn = styled.div`
  position: sticky;
  top: 120px;
  height: fit-content;
  padding-right: 2rem;
  
  @media (max-width: 992px) {
    position: relative;
    top: 0;
    padding-right: 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const ProcessLabel = styled.span`
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const ProcessHeading = styled.h2`
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1;
  margin-bottom: 1.5rem;
  color: #111;
  letter-spacing: -0.03em;
  text-transform: uppercase;
  
  @media (max-width: 992px) {
    font-size: 2.25rem;
    text-align: center;
    
    br {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 1.85rem;
  }
`;

const HighlightText = styled.span`
  color: var(--primary-color);
`;

const ProcessIntro = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #555;
  max-width: 450px;
  
  @media (max-width: 992px) {
    max-width: 100%;
    margin: 0 auto 1rem;
    font-size: 1rem;
    text-align: center;
    display: none; // Hide on mobile to reduce clutter
  }
`;

const CardsColumn = styled.div`
  position: relative;
  min-height: 200vh;
  
  @media (max-width: 992px) {
    min-height: auto;
    display: none;
  }
`;

const CardWrapper = styled.div<{ $index: number }>`
  position: sticky;
  top: ${props => 100 + (props.$index * 20)}px;
  z-index: ${props => props.$index + 1};
  margin-bottom: 1.5rem;
  backface-visibility: hidden;
  
  @media (max-width: 992px) {
    position: relative;
    top: 0;
  }
`;

const CardInner = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  
  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

// Mobile vertical cards with animations
const MobileCarousel = styled.div`
  display: none;
  
  @media (max-width: 992px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 400px;
    margin: 0 auto;
    width: 100%;
  }
`;

const MobileCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  will-change: transform, opacity;
`;

const MobileCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MobileCardNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--primary-color), #ff8f42);
  color: white;
  font-size: 1.3rem;
  font-weight: 900;
  flex-shrink: 0;
  box-shadow: 0 6px 20px rgba(255, 107, 0, 0.35);
  transform-style: preserve-3d;
  backface-visibility: hidden;
`;

const MobileCardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #222;
  margin: 0;
  line-height: 1.3;
`;

const MobileCardDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #555;
  margin: 0;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  color: #222;
  margin: 0;
  line-height: 1.3;
  position: relative;
  padding-bottom: 1rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const CardNumber = styled.span`
  font-size: 2.5rem;
  font-weight: 900;
  color: var(--primary-color);
  opacity: 0.9;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CardDescription = styled.p`
  font-size: 1.15rem;
  line-height: 1.8;
  color: #555;
  margin: 0;
`;

const ProcessSteps: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const mobileCardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 992);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // GSAP animations for mobile
  useLayoutEffect(() => {
    if (!isMobile) return;

    const ctx = gsap.context(() => {
      mobileCardsRef.current.forEach((card, index) => {
        if (!card) return;
        
        const numberEl = card.querySelector('[data-number]') as HTMLElement;
        const titleEl = card.querySelector('h3');
        const descEl = card.querySelector('p');

        // Card entrance with scrub-based parallax
        gsap.fromTo(card,
          { 
            opacity: 0, 
            y: 60,
            scale: 0.95
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: "none",
            scrollTrigger: {
              trigger: card,
              start: "top 95%",
              end: "top 60%",
              scrub: 0.5,
            }
          }
        );

        // Number flip animation on enter
        if (numberEl) {
          gsap.fromTo(numberEl,
            { 
              scale: 0, 
              rotationY: 180,
              rotationX: 90,
            },
            { 
              scale: 1, 
              rotationY: 0,
              rotationX: 0,
              duration: 0.8, 
              ease: "back.out(1.7)",
              scrollTrigger: {
                trigger: card,
                start: "top 80%",
                toggleActions: "play none none reverse"
              }
            }
          );
        }
        
        // Title slides in
        if (titleEl) {
          gsap.fromTo(titleEl,
            { opacity: 0, x: -30 },
            { 
              opacity: 1, 
              x: 0, 
              duration: 0.5, 
              ease: "power2.out",
              scrollTrigger: {
                trigger: card,
                start: "top 75%",
                toggleActions: "play none none reverse"
              }
            }
          );
        }
        
        // Description fades up
        if (descEl) {
          gsap.fromTo(descEl,
            { opacity: 0, y: 20 },
            { 
              opacity: 1, 
              y: 0, 
              duration: 0.5, 
              ease: "power2.out",
              scrollTrigger: {
                trigger: card,
                start: "top 70%",
                toggleActions: "play none none reverse"
              }
            }
          );
        }

        // Subtle exit animation as card scrolls up
        gsap.to(card, {
          opacity: 0.5,
          scale: 0.98,
          y: -20,
          ease: "none",
          scrollTrigger: {
            trigger: card,
            start: "bottom 40%",
            end: "bottom 10%",
            scrub: 0.5,
          }
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile]);

  return (
    <ProcessSection id="process" ref={sectionRef}>
      <div className="container font--jakarta">
        <ContentGrid>
          <LeftColumn>
            <ProcessHeading>
              Your <HighlightText>development</HighlightText><br />journey starts here
            </ProcessHeading>
            <ProcessIntro>
              Our journey begins with a deep dive into your vision. We engage in
              meaningful conversations to grasp your business needs, goals, and
              the outcomes you want to achieve. This phase sets the foundation
              for everything that follows.
            </ProcessIntro>
          </LeftColumn>

          {/* Desktop: Stacking cards */}
          <CardsColumn>
            {PROCESS_PHASES.map((phase, index) => (
              <CardWrapper key={phase.id} $index={index}>
                <CardInner>
                  <CardHeader>
                    <CardTitle>{phase.title}</CardTitle>
                    <CardNumber>{String(index + 1).padStart(2, '0')}</CardNumber>
                  </CardHeader>
                  <CardDescription>{phase.description}</CardDescription>
                </CardInner>
              </CardWrapper>
            ))}
          </CardsColumn>

          {/* Mobile: Cards with scroll animations */}
          <MobileCarousel>
            {PROCESS_PHASES.map((phase, index) => (
              <MobileCard 
                key={phase.id}
                ref={el => mobileCardsRef.current[index] = el}
              >
                <MobileCardHeader>
                  <MobileCardNumber data-number>
                    {String(index + 1).padStart(2, '0')}
                  </MobileCardNumber>
                  <MobileCardTitle>{phase.title}</MobileCardTitle>
                </MobileCardHeader>
                <MobileCardDescription>{phase.description}</MobileCardDescription>
              </MobileCard>
            ))}
          </MobileCarousel>
        </ContentGrid>
      </div>
    </ProcessSection>
  );
};

export default ProcessSteps;
